// Veri & Analitik → İkas: mağaza satış özeti, ürün/stok listesi, siparişler.
// Backend: /api/v1/ai-advisor/ikas/{overview,products,orders} (ikas_router.py). Kişisel müşteri verisi içermez.
import React from 'react';
import axios from 'axios';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { T, MONO, tint } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useIsMobile } from './poyais-ui/hooks';
import { useLang, addTranslations } from './i18n';
import { GrowthStyles, SectionCard, SkeletonPage, ErrorState, Delta, LegendDot, cardStyle, tooltipStyle } from './GrowthUI';

addTranslations({
  'Satış (Ciro)': 'Sales (Revenue)',
  'Sipariş': 'Orders',
  'Sepet Ortalaması': 'Avg. Order Value',
  'İptal / İade': 'Cancelled / Refunded',
  'önceki döneme göre': 'vs previous period',
  'Günlük Satış': 'Daily Sales',
  'Ciro': 'Revenue',
  'Trafik Kaynakları': 'Traffic Sources',
  'Siparişin UTM kaynağından; kaynak yoksa yönlendiren site, o da yoksa direkt.': 'From the order’s UTM source; falls back to referrer, then direct.',
  'Kaynak': 'Source',
  'Pay': 'Share',
  'En Çok Satan Ürünler': 'Top Products',
  'adet': 'units',
  'Bu dönemde satış yok.': 'No sales in this period.',
  'Ödenmemiş': 'Unpaid',
  'Ürün ara…': 'Search products…',
  'Düşük Stok': 'Low Stock',
  'Stoğu 5 ve altında olan ürünleri gösterir; tekrar tıklayınca kapanır.': 'Shows products with 5 or fewer in stock; click again to clear.',
  'Tümü': 'All',
  'ürün': 'products',
  'Stok': 'Stock',
  'Fiyat': 'Price',
  'Durum': 'Status',
  'Aktif': 'Active',
  'Pasif': 'Inactive',
  'varyant': 'variants',
  'Ürün bulunamadı.': 'No products found.',
  'Sipariş No': 'Order No',
  'Tarih': 'Date',
  'Tutar': 'Amount',
  'Ödeme': 'Payment',
  'Ürünler': 'Products',
  'Sayılmayan sipariş (iptal, iade, ödenmemiş)': 'Not counted as a sale (cancelled, refunded, unpaid)',
  'Sipariş bulunamadı.': 'No orders found.',
  'İkas bağlı değil': 'ikas not connected',
  'Ayarlar → Entegrasyonlar bölümünden ikas hesabını bağla.': 'Connect your ikas account under Settings → Integrations.',
  'ikas verisi yüklenemedi': 'Could not load ikas data',
  'Gösterilen': 'Showing',
});

const API = '/api/v1/ai-advisor/ikas';
const CUR: Record<string, string> = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };
const money = (v: number | null | undefined, cur = 'TRY') => (CUR[cur] || cur + ' ') + Math.round(v || 0).toLocaleString('tr-TR');
const dayLabel = (d: string) => { const x = new Date(d + 'T00:00:00'); return `${x.getDate()}.${String(x.getMonth() + 1).padStart(2, '0')}`; };

const ORDER_STATUS: Record<string, string> = { CREATED: 'Oluşturuldu', CANCELLED: 'İptal', REFUNDED: 'İade', PARTIALLY_REFUNDED: 'Kısmi iade', PARTIALLY_CANCELLED: 'Kısmi iptal', DRAFT: 'Taslak', REFUND_REQUESTED: 'İade talebi', REFUND_REJECTED: 'İade reddedildi', WAITING_UPSELL_ACTION: 'Upsell bekliyor' };
const PAY_STATUS: Record<string, string> = { PAID: 'Ödendi', WAITING: 'Bekliyor', FAILED: 'Başarısız', PARTIALLY_PAID: 'Kısmi ödeme', REFUNDED: 'İade edildi', OVER_PAID: 'Fazla ödeme' };

function useIkas<D>(path: string, params: Record<string, any>, deps: any[]) {
  const [data, setData] = React.useState<D | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  React.useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    axios.get(`${API}/${path}`, { params })
      .then(r => alive && setData(r.data))
      .catch(e => alive && setError(e?.response?.data?.detail || 'ikas verisi yüklenemedi'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, error };
}

function Pill({ children, color, bg }: { children: React.ReactNode; color: string; bg: string }) {
  return <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 800, color, background: bg, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>{children}</span>;
}

function Kpi({ icon, color, label, value, delta, sub }: { icon: React.ReactNode; color: string; label: string; value: string; delta?: number | null; sub?: React.ReactNode }) {
  const { t } = useLang();
  return (
    <div className="gr-hover" style={{ ...cardStyle, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 30, height: 30, borderRadius: 9, background: tint(color, 15), color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.textMid }}>{label}</span>
      </div>
      <span style={{ fontSize: 'clamp(19px, 2.2vw, 25px)', fontWeight: 800, color: T.text, fontFamily: MONO, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
      {delta !== undefined && <Delta value={delta} suffix={t('önceki döneme göre')} />}
      {sub}
    </div>
  );
}

type Overview = {
  currency: string;
  summary: { orders: number; revenue: number; aov: number; orders_delta_pct: number | null; revenue_delta_pct: number | null; aov_delta_pct: number | null; all_orders: number; cancelled: number; refunded: number; unpaid: number };
  daily: { date: string; revenue: number; orders: number }[];
  sources: { source: string; orders: number; revenue: number }[];
  top_products: { name: string; qty: number; revenue: number }[];
};

function OverviewView({ clientId, start, end }: { clientId: number; start: string; end: string }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const { data, loading, error } = useIkas<Overview>('overview', { client_id: clientId, start, end }, [clientId, start, end]);
  if (loading) return <SkeletonPage isMobile={isMobile} />;
  if (error || !data) return <ErrorState error={error || t('ikas verisi yüklenemedi')} />;
  const s = data.summary, cur = data.currency;
  const totalRev = data.sources.reduce((a, x) => a + x.revenue, 0) || 1;
  const maxProd = Math.max(1, ...data.top_products.map(p => p.revenue));
  const axis = { fontSize: 10.5, fill: T.textDim as any };
  return (
    <div className="gr-fade">
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(4, minmax(0,1fr))', gap: isMobile ? 10 : 12, marginBottom: 14 }}>
        <Kpi icon={<Icon.chart size={15} sw={2.1} />} color={T.blue} label={t('Satış (Ciro)')} value={money(s.revenue, cur)} delta={s.revenue_delta_pct} />
        <Kpi icon={<Icon.checklist size={15} />} color={T.green} label={t('Sipariş')} value={s.orders.toLocaleString('tr-TR')} delta={s.orders_delta_pct} />
        <Kpi icon={<Icon.invoice size={15} />} color={T.amber} label={t('Sepet Ortalaması')} value={money(s.aov, cur)} delta={s.aov_delta_pct} />
        <Kpi icon={<Icon.warningTriangle size={15} />} color={T.red} label={t('İptal / İade')} value={`${s.cancelled} / ${s.refunded}`}
          sub={s.unpaid > 0 ? <span style={{ fontSize: 11.5, color: T.amberDeepText, fontWeight: 700 }}>{s.unpaid} {t('Ödenmemiş').toLowerCase()}</span> : undefined} />
      </div>

      <SectionCard style={{ marginBottom: 14 }} pad={isMobile ? 14 : 18} title={t('Günlük Satış')}
        right={<div style={{ display: 'flex', gap: 12 }}><LegendDot color={tint(T.blue, 45)} label={t('Ciro')} /><LegendDot line color={T.green} label={t('Sipariş')} /></div>}>
        {s.orders === 0 ? <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 13, color: T.textDim }}>{t('Bu dönemde satış yok.')}</div> : (
          <ResponsiveContainer width="100%" height={isMobile ? 230 : 300}>
            <ComposedChart data={data.daily} margin={{ top: 10, right: isMobile ? 0 : 8, bottom: 0, left: isMobile ? -14 : 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.divider as any} vertical={false} />
              <XAxis dataKey="date" tickFormatter={dayLabel} tick={axis} axisLine={false} tickLine={false} minTickGap={14} />
              <YAxis yAxisId="rev" tick={axis} axisLine={false} tickLine={false} width={isMobile ? 50 : 62} tickFormatter={(v) => money(v, cur)} />
              <YAxis yAxisId="ord" orientation="right" allowDecimals={false} tick={{ ...axis, fill: T.greenText as any }} axisLine={false} tickLine={false} width={28} />
              <Tooltip content={({ active, payload, label }: any) => active && payload?.length ? (
                <div style={tooltipStyle}><div style={{ fontWeight: 800, color: T.text, marginBottom: 4 }}>{dayLabel(label)}</div>
                  <div style={{ color: T.blueText, fontWeight: 600 }}>{t('Ciro')}: {money(payload.find((p: any) => p.dataKey === 'revenue')?.value, cur)}</div>
                  <div style={{ color: T.greenText, fontWeight: 600 }}>{t('Sipariş')}: {payload.find((p: any) => p.dataKey === 'orders')?.value}</div></div>) : null} />
              <Bar yAxisId="rev" dataKey="revenue" fill={tint(T.blue, 45) as any} radius={[5, 5, 0, 0]} isAnimationActive={false} />
              <Line yAxisId="ord" dataKey="orders" stroke={T.green as any} strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </SectionCard>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'repeat(2, minmax(0,1fr))', gap: 14, alignItems: 'stretch' }}>
        <SectionCard title={t('Trafik Kaynakları')} sub={t('Siparişin UTM kaynağından; kaynak yoksa yönlendiren site, o da yoksa direkt.')} pad={isMobile ? 14 : 18}>
          {data.sources.length === 0 ? <div style={{ fontSize: 13, color: T.textDim }}>{t('Bu dönemde satış yok.')}</div> : data.sources.map(x => (
            <div key={x.source} style={{ padding: '9px 0', borderTop: `1px solid ${T.divider}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{x.source}</span>
                <span style={{ fontFamily: MONO, fontWeight: 800, color: T.text, whiteSpace: 'nowrap' }}>{money(x.revenue, cur)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
                <div style={{ flex: 1, height: 5, background: T.trackBg, borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${x.revenue / totalRev * 100}%`, height: '100%', background: T.blue, opacity: 0.75, borderRadius: 999 }} /></div>
                <span style={{ fontSize: 11, color: T.textDim, whiteSpace: 'nowrap' }}>{x.orders} {t('Sipariş').toLowerCase()} · %{Math.round(x.revenue / totalRev * 100)}</span>
              </div>
            </div>
          ))}
        </SectionCard>
        <SectionCard title={t('En Çok Satan Ürünler')} pad={isMobile ? 14 : 18}>
          {data.top_products.length === 0 ? <div style={{ fontSize: 13, color: T.textDim }}>{t('Bu dönemde satış yok.')}</div> : data.top_products.map((p, i) => (
            <div key={i} style={{ padding: '9px 0', borderTop: i ? `1px solid ${T.divider}` : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{p.name}</span>
                <span style={{ fontFamily: MONO, fontWeight: 800, color: T.text, whiteSpace: 'nowrap' }}>{money(p.revenue, cur)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
                <div style={{ flex: 1, height: 5, background: T.trackBg, borderRadius: 999, overflow: 'hidden' }}><div style={{ width: `${p.revenue / maxProd * 100}%`, height: '100%', background: T.green, opacity: 0.75, borderRadius: 999 }} /></div>
                <span style={{ fontSize: 11, color: T.textDim, whiteSpace: 'nowrap' }}>{p.qty} {t('adet')}</span>
              </div>
            </div>
          ))}
        </SectionCard>
      </div>
    </div>
  );
}

type Product = { id: string; name: string; brand?: string | null; categories: string[]; stock: number; low_stock: boolean; active: boolean; variant_count: number; price_min: number | null; price_max: number | null };

function ProductsView({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const { data, loading, error } = useIkas<{ total: number; low_stock_count: number; products: Product[] }>('products', { client_id: clientId }, [clientId]);
  const [q, setQ] = React.useState('');
  const [lowOnly, setLowOnly] = React.useState(false);
  if (loading) return <SkeletonPage isMobile={isMobile} kpis={3} />;
  if (error || !data) return <ErrorState error={error || t('ikas verisi yüklenemedi')} />;
  const list = data.products.filter(p => (!lowOnly || p.low_stock) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));
  const price = (p: Product) => p.price_min == null ? '—' : p.price_min === p.price_max ? money(p.price_min) : `${money(p.price_min)} – ${money(p.price_max)}`;
  return (
    <div className="gr-fade">
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 420 }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.textDim, display: 'flex' }}><Icon.search size={15} /></span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('Ürün ara…')}
            style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 11, border: `1px solid ${T.borderMid}`, background: T.surface, color: T.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <button onClick={() => setLowOnly(v => !v)} title={t('Stoğu 5 ve altında olan ürünleri gösterir; tekrar tıklayınca kapanır.')} style={{ padding: '9px 14px', borderRadius: 11, border: `1px solid ${lowOnly ? T.amber : T.borderMid}`, background: lowOnly ? T.amberTint : T.surface,
          color: lowOnly ? T.amberDeepText : T.textMid, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          {t('Düşük Stok')} ({data.low_stock_count})
        </button>
        <span style={{ fontSize: 12, color: T.textDim, marginLeft: 'auto' }}>{list.length} / {data.total} {t('ürün')}</span>
      </div>
      {list.length === 0 ? <div style={{ ...cardStyle, padding: 40, textAlign: 'center', fontSize: 13, color: T.textDim }}>{t('Ürün bulunamadı.')}</div> : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map(p => (
            <div key={p.id} style={{ ...cardStyle, padding: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>{p.name}</span>
                <Pill color={p.active ? T.greenText : T.textDim} bg={p.active ? T.greenTint : T.subtleBg}>{p.active ? t('Aktif') : t('Pasif')}</Pill>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12.5 }}>
                <span style={{ color: T.textMid }}>{t('Fiyat')}: <b style={{ fontFamily: MONO, color: T.text }}>{price(p)}</b></span>
                <Pill color={p.low_stock ? T.amberDeepText : T.textMid} bg={p.low_stock ? T.amberTint : T.subtleBg}>{t('Stok')}: {p.stock}</Pill>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ color: T.textDim, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {[t('Ürünler'), t('Fiyat'), t('Stok'), t('Durum')].map((h, i) => <th key={h} style={{ padding: '11px 14px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 800, borderBottom: `1px solid ${T.border}` }}>{h}</th>)}
            </tr></thead>
            <tbody>{list.map(p => (
              <tr key={p.id} className="gr-row" style={{ borderBottom: `1px solid ${T.divider}` }}>
                <td style={{ padding: '11px 14px' }}><div style={{ fontWeight: 700, color: T.text }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: T.textDim }}>{[p.brand, p.categories.join(', '), p.variant_count > 1 ? `${p.variant_count} ${t('varyant')}` : ''].filter(Boolean).join(' · ')}</div></td>
                <td style={{ padding: '11px 14px', textAlign: 'right', fontFamily: MONO, fontWeight: 700, color: T.text }}>{price(p)}</td>
                <td style={{ padding: '11px 14px', textAlign: 'right' }}><Pill color={p.low_stock ? T.amberDeepText : T.textMid} bg={p.low_stock ? T.amberTint : T.subtleBg}>{p.stock}</Pill></td>
                <td style={{ padding: '11px 14px', textAlign: 'right' }}><Pill color={p.active ? T.greenText : T.textDim} bg={p.active ? T.greenTint : T.subtleBg}>{p.active ? t('Aktif') : t('Pasif')}</Pill></td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type OrderRow = { id: string; number: string; date: string; total: number; currency: string; status: string; payment: string; source: string; lines: string[]; counted_as_sale: boolean };

function OrdersView({ clientId, start, end }: { clientId: number; start: string; end: string }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const { data, loading, error } = useIkas<{ total: number; orders: OrderRow[] }>('orders', { client_id: clientId, start, end, limit: 100 }, [clientId, start, end]);
  if (loading) return <SkeletonPage isMobile={isMobile} kpis={3} />;
  if (error || !data) return <ErrorState error={error || t('ikas verisi yüklenemedi')} />;
  if (!data.orders.length) return <div style={{ ...cardStyle, padding: 40, textAlign: 'center', fontSize: 13, color: T.textDim }}>{t('Sipariş bulunamadı.')}</div>;
  const st = (o: OrderRow) => <Pill color={o.counted_as_sale ? T.greenText : T.redText} bg={o.counted_as_sale ? T.greenTint : T.redTint}>{ORDER_STATUS[o.status] || o.status}</Pill>;
  const pay = (o: OrderRow) => <Pill color={o.payment === 'PAID' ? T.greenText : T.amberDeepText} bg={o.payment === 'PAID' ? T.greenTint : T.amberTint}>{PAY_STATUS[o.payment] || o.payment}</Pill>;
  return (
    <div className="gr-fade">
      <div style={{ fontSize: 12, color: T.textDim, marginBottom: 10 }}>{t('Gösterilen')}: {data.orders.length} / {data.total}</div>
      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.orders.map(o => (
            <div key={o.id} style={{ ...cardStyle, padding: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                <span style={{ fontWeight: 800, color: T.text }}>#{o.number}</span>
                <span style={{ fontFamily: MONO, fontWeight: 800, color: T.text }}>{money(o.total, o.currency)}</span>
              </div>
              <div style={{ fontSize: 11.5, color: T.textDim, margin: '4px 0 8px' }}>{o.date} · {o.source}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{st(o)}{pay(o)}</div>
              <div style={{ fontSize: 12, color: T.textMid, marginTop: 8 }}>{o.lines.join(', ')}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ color: T.textDim, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {[t('Sipariş No'), t('Tarih'), t('Ürünler'), t('Kaynak'), t('Tutar'), t('Durum'), t('Ödeme')].map((h, i) => <th key={h} style={{ padding: '11px 12px', textAlign: i === 4 ? 'right' : 'left', fontWeight: 800, borderBottom: `1px solid ${T.border}` }}>{h}</th>)}
            </tr></thead>
            <tbody>{data.orders.map(o => (
              <tr key={o.id} className="gr-row" style={{ borderBottom: `1px solid ${T.divider}` }} title={o.counted_as_sale ? '' : t('Sayılmayan sipariş (iptal, iade, ödenmemiş)')}>
                <td style={{ padding: '10px 12px', fontWeight: 800, color: T.text }}>#{o.number}</td>
                <td style={{ padding: '10px 12px', color: T.textMid, whiteSpace: 'nowrap' }}>{o.date}</td>
                <td style={{ padding: '10px 12px', color: T.textMid, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.lines.join(', ')}</td>
                <td style={{ padding: '10px 12px', color: T.textMid }}>{o.source}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: MONO, fontWeight: 800, color: T.text }}>{money(o.total, o.currency)}</td>
                <td style={{ padding: '10px 12px' }}>{st(o)}</td>
                <td style={{ padding: '10px 12px' }}>{pay(o)}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function IkasTab({ tab, clientId, start, end }: { tab: 'overview' | 'products' | 'orders'; clientId: number; start: string; end: string }) {
  return (
    <div>
      <GrowthStyles />
      {tab === 'overview' && <OverviewView clientId={clientId} start={start} end={end} />}
      {tab === 'products' && <ProductsView clientId={clientId} />}
      {tab === 'orders' && <OrdersView clientId={clientId} start={start} end={end} />}
    </div>
  );
}
